import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn, Modal, Field, Input, Select } from "@/components/ui-kit";
import { formatINR, formatDate } from "@/lib/format";
import { Plus, Trash2, Fuel as FuelIcon } from "lucide-react";

const empty = { date: "", vehicle_id: "", driver_id: "", quantity_liters: 0, cost: 0, odometer: 0, notes: "" };

export default function Fuel() {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [f, v, d] = await Promise.all([api.get("/owner/fuel"), api.get("/owner/vehicles"), api.get("/owner/drivers")]);
    setItems(f.data); setVehicles(v.data); setDrivers(d.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    const data = { ...editing, quantity_liters: Number(editing.quantity_liters), cost: Number(editing.cost), odometer: Number(editing.odometer || 0) };
    if (!data.driver_id) data.driver_id = null;
    await api.post("/owner/fuel", data);
    setOpen(false); setEditing(null); load();
  };

  const del = async (id) => { if (confirm("Delete fuel entry?")) { await api.delete(`/owner/fuel/${id}`); load(); } };

  const mileage = (entry) => (entry.quantity_liters && entry.odometer) ? (entry.odometer / entry.quantity_liters).toFixed(1) : "—";

  return (
    <div data-testid="fuel-page">
      <PageHeader title="Fuel Entries" subtitle="Per-vehicle fuel logs for mileage and cost analysis."
        actions={<Btn data-testid="fuel-add" onClick={() => { setEditing({...empty, date: new Date().toISOString().slice(0,10)}); setOpen(true); }}><Plus className="h-4 w-4" /> Add Fuel</Btn>}
      />
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="tbl">
          <thead><tr><th>Date</th><th>Vehicle</th><th>Driver</th><th>Liters</th><th>Cost</th><th>Odometer</th><th>Per Litre</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-zinc-500"><FuelIcon className="h-8 w-8 mx-auto mb-2 text-zinc-300" /> No entries yet.</td></tr>
            ) : items.map((f) => {
              const v = vehicles.find((x) => x.id === f.vehicle_id);
              const d = drivers.find((x) => x.id === f.driver_id);
              return (
                <tr key={f.id}>
                  <td>{formatDate(f.date)}</td>
                  <td className="font-mono">{v?.registration_number || "—"}</td>
                  <td>{d?.name || "—"}</td>
                  <td>{f.quantity_liters} L</td>
                  <td className="font-mono">{formatINR(f.cost)}</td>
                  <td>{f.odometer || "—"}</td>
                  <td className="font-mono">{f.quantity_liters ? `₹${(f.cost / f.quantity_liters).toFixed(1)}` : "—"}</td>
                  <td className="text-right"><Btn variant="ghost" onClick={() => del(f.id)} data-testid={`fuel-delete-${f.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Btn></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Fuel Entry" testid="fuel-modal"
        footer={<><Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn><Btn data-testid="fuel-save" onClick={submit}>Save</Btn></>}>
        {editing && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Date" required><Input type="date" value={editing.date} onChange={(e) => setEditing({...editing, date: e.target.value})} required /></Field>
            <Field label="Vehicle" required>
              <Select value={editing.vehicle_id} onChange={(e) => setEditing({...editing, vehicle_id: e.target.value})} required data-testid="fuel-vehicle">
                <option value="">— Select —</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.registration_number}</option>)}
              </Select>
            </Field>
            <Field label="Driver">
              <Select value={editing.driver_id || ""} onChange={(e) => setEditing({...editing, driver_id: e.target.value})}>
                <option value="">—</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </Field>
            <Field label="Quantity (Litres)" required><Input type="number" step="0.01" value={editing.quantity_liters} onChange={(e) => setEditing({...editing, quantity_liters: e.target.value})} required /></Field>
            <Field label="Cost (INR)" required><Input type="number" step="0.01" value={editing.cost} onChange={(e) => setEditing({...editing, cost: e.target.value})} required /></Field>
            <Field label="Odometer"><Input type="number" value={editing.odometer || ""} onChange={(e) => setEditing({...editing, odometer: e.target.value})} /></Field>
          </form>
        )}
      </Modal>
    </div>
  );
}
